/**
 * What the Zunox tab and the engine service pass each other through the
 * Hub, byte for byte as `zunox-puller/src/items.rs` reads and writes them.
 *
 * Every item's body is encrypted to the client's key (the same envelope a
 * computer's records use), so the Hub can't read it. Inside, a code that
 * only its real sender could make stops the Hub passing off one of its own:
 *
 * - Staff's commands (settings, the check's answers, findings, requests)
 *   carry a code from the key the staff member and the engine account share
 *   (X25519 between their two account keys). The engine takes commands only
 *   from staff whose key it was told to trust, and only commands meant for
 *   it.
 * - The engine's replies (the client's files and its results) carry a code
 *   from the client's own key, which the Hub never holds; this page checks
 *   it with the key it already holds for the client.
 *
 * Body: "ZXE1", the header's length (4 bytes, big-endian), the header (JSON),
 * the payload's length, the payload (gzipped JSON), then the code:
 * HMAC-SHA256 over everything before it. The header names the client, the
 * kind and label the Hub lists it under, who made it and when, so an item
 * can't be moved to another client or relabelled either.
 */
import { x25519 } from '@noble/curves/ed25519';
import { hkdf } from '@noble/hashes/hkdf';
import { hmac } from '@noble/hashes/hmac';
import { sha256 } from '@noble/hashes/sha256';
import { base64ToBytes, bytesToBase64, concatBytes } from '~/util/bytes';
import { decryptOrgSummary, encryptOrgSummary, gunzip, publicKeyFromPrivate, } from '~/util/orgKeypair';
import { encryptionKeyOf, uuidBytes, verifySignature } from '~/util/orgSigning';
const encoder = new TextEncoder();
const decoder = new TextDecoder();
const MAGIC = encoder.encode('ZXE1');
const STAFF_INFO = encoder.encode('zunox/engine-item/staff/v1');
const ENGINE_INFO = encoder.encode('zunox/engine-item/engine/v1');
const MAC_BYTES = 32;
const SIGNATURE_BYTES = 64;
/** Leads what the engine signs (zunox-puller items::REPLY_CONTEXT). */
const REPLY_CONTEXT = encoder.encode('zunox/engine-reply/v1\0');
export class EngineItemError extends Error {
}
/** base64: the public half of an account key. */
export function accountPublicKey(privateKey) {
    return bytesToBase64(x25519.getPublicKey(privateKey));
}
/** The code key a staff member and the engine share for one client. */
export function staffMacKey(staffPrivate, enginePublic, orgId) {
    const shared = x25519.getSharedSecret(staffPrivate, enginePublic);
    return hkdf(sha256, shared, uuidBytes(orgId), STAFF_INFO, 32);
}
/** The code key for the engine's replies: from the client's own key. */
export function engineMacKey(orgEncryptionKey, orgId) {
    return hkdf(sha256, orgEncryptionKey, uuidBytes(orgId), ENGINE_INFO, 32);
}
function u32(n) {
    const out = new Uint8Array(4);
    new DataView(out.buffer).setUint32(0, n, false);
    return out;
}
export function encodeItem(header, payload, macKey) {
    const headerBytes = encoder.encode(JSON.stringify(header));
    const signed = concatBytes(MAGIC, u32(headerBytes.length), headerBytes, u32(payload.length), payload);
    return concatBytes(signed, hmac(sha256, macKey, signed));
}
export function decodeItem(bytes) {
    // A version 1 body ends in one code, a version 2 body in two; the header
    // says which, and must agree.
    let firstError = null;
    for (const version of [1, 2]) {
        try {
            const item = decodeWith(bytes, version);
            if (item.header.v === version)
                return item;
        }
        catch (err) {
            firstError = firstError ?? err;
        }
    }
    throw firstError instanceof EngineItemError
        ? firstError
        : new EngineItemError('Not an engine item: wrong format.');
}
function decodeWith(bytes, version) {
    const bad = (why) => new EngineItemError(`Not an engine item: ${why}.`);
    const trailer = version === 2 ? MAC_BYTES + SIGNATURE_BYTES : MAC_BYTES;
    if (bytes.length < MAGIC.length + 8 + trailer)
        throw bad('too short');
    if (!MAGIC.every((b, i) => bytes[i] === b))
        throw bad('wrong format');
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    let at = MAGIC.length;
    const headerLength = view.getUint32(at, false);
    at += 4;
    if (at + headerLength + 4 > bytes.length - trailer)
        throw bad('header runs past the end');
    const headerBytes = bytes.subarray(at, at + headerLength);
    at += headerLength;
    const payloadLength = view.getUint32(at, false);
    at += 4;
    if (at + payloadLength !== bytes.length - trailer)
        throw bad('payload runs past the end');
    const payload = bytes.subarray(at, at + payloadLength);
    let header;
    try {
        header = JSON.parse(decoder.decode(headerBytes));
    }
    catch {
        throw bad('its header is not JSON');
    }
    const end = bytes.length - trailer;
    return {
        header,
        payload,
        signed: bytes.subarray(0, end),
        mac: bytes.subarray(end, end + MAC_BYTES),
        signature: version === 2 ? bytes.subarray(end + MAC_BYTES) : null,
    };
}
/** Whether the engine's signature is there and is `signingKeyBase64`'s. */
export function signedBy(item, signingKeyBase64) {
    if (!item.signature)
        return false;
    return verifySignature(signingKeyBase64, concatBytes(REPLY_CONTEXT, item.signed), bytesToBase64(item.signature));
}
function sameBytes(a, b) {
    if (a.length !== b.length)
        return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++)
        diff |= a[i] ^ b[i];
    return diff === 0;
}
export function macMatches(item, macKey) {
    return sameBytes(hmac(sha256, macKey, item.signed), item.mac);
}
export async function gzip(bytes) {
    const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}
/** A command for the engine, sealed for the Hub: locked to the client's
 * key, with the code only this staff member and the engine can make. */
export async function sealCommand(args) {
    const header = {
        v: 1,
        org: args.orgId,
        kind: args.kind,
        label: args.label,
        made_at: (args.madeAt ?? new Date()).toISOString(),
        reply_to: null,
        by: 'staff',
        from: accountPublicKey(args.staffPrivate),
        to: args.enginePublic,
        seq: 0,
    };
    const payload = await gzip(encoder.encode(JSON.stringify(args.payload)));
    const key = staffMacKey(args.staffPrivate, base64ToBytes(args.enginePublic), args.orgId);
    const inner = encodeItem(header, payload, key);
    return encryptOrgSummary(publicKeyFromPrivate(args.secret), args.keyVersion, inner);
}
/** One of the engine's replies, unlocked with the client's key and checked:
 * the code is the engine's, it's signed with the signing key staff verified
 * for the engine (anyone holding the client's key could make the code, only
 * the engine the signature), and it names this client, kind and label. */
export async function openReply(secret, orgId, item, engineSigningKey) {
    const inner = await decryptOrgSummary(secret, item);
    const decoded = decodeItem(inner);
    const h = decoded.header;
    if (!macMatches(decoded, engineMacKey(encryptionKeyOf(secret), orgId))) {
        throw new EngineItemError("This result doesn't carry the engine's code: it wasn't made by the engine.");
    }
    if (!signedBy(decoded, engineSigningKey)) {
        throw new EngineItemError("This result isn't signed by the engine you verified: it wasn't made by it.");
    }
    if ((h.v !== 1 && h.v !== 2) ||
        h.by !== 'engine' ||
        h.org !== orgId ||
        h.kind !== item.kind ||
        h.label !== item.label) {
        throw new EngineItemError('This result was made for another client or listed as something else.');
    }
    let payload;
    try {
        payload = JSON.parse(decoder.decode(await gunzip(decoded.payload)));
    }
    catch {
        throw new EngineItemError("This result's contents can't be read.");
    }
    return { header: h, payload };
}
/**
 * An engine item's body, moved from one key of the client to the next (after
 * a rotation), or null if it can't be: the engine's replies carry a code from
 * the client's key, so the old code is checked and a new one made with the
 * new key; staff's commands carry their own code and go across unchanged.
 * Without this the engine couldn't open its own files again after a rotation.
 */
export function moveItemToNewKey(plaintext, listed, orgId, oldEncryptionKey, newEncryptionKey) {
    let decoded;
    try {
        decoded = decodeItem(plaintext);
    }
    catch {
        return null;
    }
    const h = decoded.header;
    if (h.org !== orgId || h.kind !== listed.kind || h.label !== listed.label)
        return null;
    if (h.by === 'staff')
        return plaintext;
    if (h.by !== 'engine' || !macMatches(decoded, engineMacKey(oldEncryptionKey, orgId)))
        return null;
    // The client-key code is made again with the new key; the engine's
    // signature (version 2) goes across as it is: nobody here can make one,
    // and the engine and the tab still check it.
    return concatBytes(decoded.signed, hmac(sha256, engineMacKey(newEncryptionKey, orgId), decoded.signed), decoded.signature ?? new Uint8Array(0));
}
